/**
 * BulkEditModal — modal con dropdowns en cascada para editar campos del
 * catálogo en bulk.
 *
 * Campos editables: catalog_category → subcategory → segmento, con udm
 * derivado 1:1 de subcategory. Esto matchea la taxonomía Samsung v6.7 y se
 * extiende a otros clientes con la misma estructura.
 *
 * Fuente de las opciones: la prop `taxonomy` se pasa desde el componente
 * padre (DataTable / Publicaciones). Si no se pasa, los dropdowns se renderean
 * como inputs de texto libre (fallback no-Samsung).
 *
 * Spec: el dropdown UDM es read-only — derivado de la subcategoría
 * seleccionada (mapping 1:1).
 */
import React, { useMemo, useState } from 'react';
import { X, Loader2, Edit3 } from 'lucide-react';

export interface TaxonomySegment {
  value: string;
  udm?: string;
  segmento?: string[];
}

export interface TaxonomyCategory {
  value: string;
  subcategory: TaxonomySegment[];
}

export interface TaxonomyTree {
  catalog_category: TaxonomyCategory[];
}

export interface BulkEditPayload {
  catalog_category?: string | null;
  subcategory?: string | null;
  segmento?: string | null;
  udm?: string | null;
}

interface Props {
  isOpen: boolean;
  count: number;
  taxonomy: TaxonomyTree | null;
  onClose: () => void;
  onConfirm: (fields: BulkEditPayload, comment: string) => Promise<void>;
}

const SENTINEL = '__keep__';

const BulkEditModal: React.FC<Props> = ({
  isOpen,
  count,
  taxonomy,
  onClose,
  onConfirm,
}) => {
  const [catalogCategory, setCatalogCategory] = useState<string>(SENTINEL);
  const [subcategory, setSubcategory] = useState<string>(SENTINEL);
  const [segmento, setSegmento] = useState<string>(SENTINEL);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Cascada: opciones de subcategoría según categoría seleccionada
  const subcatOptions = useMemo<TaxonomySegment[]>(() => {
    if (!taxonomy || catalogCategory === SENTINEL) return [];
    const cat = taxonomy.catalog_category.find((c) => c.value === catalogCategory);
    return cat?.subcategory || [];
  }, [taxonomy, catalogCategory]);

  // Cascada: opciones de segmento según subcategoría seleccionada
  const segmentoOptions = useMemo<string[]>(() => {
    if (subcategory === SENTINEL) return [];
    const sub = subcatOptions.find((s) => s.value === subcategory);
    return sub?.segmento || [];
  }, [subcatOptions, subcategory]);

  // UDM derivado 1:1 de la subcategoría
  const derivedUdm = useMemo<string | null>(() => {
    if (subcategory === SENTINEL) return null;
    const sub = subcatOptions.find((s) => s.value === subcategory);
    return sub?.udm || null;
  }, [subcatOptions, subcategory]);

  if (!isOpen) return null;

  const reset = () => {
    setCatalogCategory(SENTINEL);
    setSubcategory(SENTINEL);
    setSegmento(SENTINEL);
    setComment('');
    setErr(null);
  };

  const handleCategoryChange = (v: string) => {
    setCatalogCategory(v);
    setSubcategory(SENTINEL);
    setSegmento(SENTINEL);
  };

  const handleSubcategoryChange = (v: string) => {
    setSubcategory(v);
    setSegmento(SENTINEL);
  };

  const handleConfirm = async () => {
    // Armar payload solo con los campos modificados (no-sentinel).
    const payload: BulkEditPayload = {};
    if (catalogCategory !== SENTINEL) payload.catalog_category = catalogCategory;
    if (subcategory !== SENTINEL) payload.subcategory = subcategory;
    if (segmento !== SENTINEL) payload.segmento = segmento;
    if (derivedUdm && subcategory !== SENTINEL) payload.udm = derivedUdm;

    if (Object.keys(payload).length === 0) {
      setErr('No hay campos para actualizar.');
      return;
    }

    setSubmitting(true);
    setErr(null);
    try {
      await onConfirm(payload, comment);
      reset();
      onClose();
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message || 'Error desconocido';
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const fallbackText = !taxonomy;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 shadow-xl border border-gray-200">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Edit3 className="w-5 h-5 text-blue-600" />
            <h3 className="text-base font-semibold text-gray-900">
              Editar {count.toLocaleString('es-AR')} item{count === 1 ? '' : 's'}
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={submitting}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-3 text-sm">
          <p className="text-xs text-gray-600">
            Solo los campos modificados se aplican. El resto queda intacto.
            Estos cambios entran a <code>manually_edited_fields</code> y no se
            sobreescriben en runs futuros.
          </p>

          {/* Categoría */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Categoría
            </label>
            {fallbackText ? (
              <input
                type="text"
                value={catalogCategory === SENTINEL ? '' : catalogCategory}
                onChange={(e) =>
                  setCatalogCategory(e.target.value === '' ? SENTINEL : e.target.value)
                }
                disabled={submitting}
                placeholder="Mantener actual"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            ) : (
              <select
                value={catalogCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                disabled={submitting}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value={SENTINEL}>— Mantener actual —</option>
                {taxonomy!.catalog_category.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.value}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Subcategoría */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Subcategoría
            </label>
            {fallbackText ? (
              <input
                type="text"
                value={subcategory === SENTINEL ? '' : subcategory}
                onChange={(e) =>
                  setSubcategory(e.target.value === '' ? SENTINEL : e.target.value)
                }
                disabled={submitting}
                placeholder="Mantener actual"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            ) : (
              <select
                value={subcategory}
                onChange={(e) => handleSubcategoryChange(e.target.value)}
                disabled={submitting || catalogCategory === SENTINEL}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value={SENTINEL}>— Mantener actual —</option>
                {subcatOptions.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.value}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Segmento */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Segmento
            </label>
            {fallbackText ? (
              <input
                type="text"
                value={segmento === SENTINEL ? '' : segmento}
                onChange={(e) =>
                  setSegmento(e.target.value === '' ? SENTINEL : e.target.value)
                }
                disabled={submitting}
                placeholder="Mantener actual"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            ) : (
              <select
                value={segmento}
                onChange={(e) => setSegmento(e.target.value)}
                disabled={submitting || subcategory === SENTINEL}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value={SENTINEL}>— Mantener actual —</option>
                {segmentoOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* UDM (derivado, read-only) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              UDM (derivado de subcategoría)
            </label>
            <input
              type="text"
              value={derivedUdm || ''}
              readOnly
              disabled
              placeholder="—"
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded bg-gray-50 text-gray-600 cursor-not-allowed"
            />
          </div>

          {/* Comentario */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Comentario (opcional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={submitting}
              rows={2}
              placeholder="Contexto para audit…"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded disabled:opacity-50"
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkEditModal;
